<template>
  <div class="ca-popover" ref="popoverRef" @keydown="onKeydown">
    <button
      class="ca-popover__trigger"
      @click="togglePopup"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
    >
      <component :is="icon" v-if="icon" class="ca-popover__icon" />
      <span class="ca-popover__label">{{ currentLabel }}</span>
    </button>

    <div
      v-show="isOpen"
      class="ca-popover__popup rounded-xs p-size-4"
      role="listbox"
      ref="popupRef"
    >
      <a
        v-for="(option, idx) in options"
        :key="option.value"
        :href="option.url"
        class="ca-popover__option rounded-xs"
        :class="{
          'ca-popover__option--active': option.value === selected,
        }"
        role="option"
        :tabindex="isOpen ? 0 : -1"
        :aria-selected="option.value === selected"
        @click.prevent="selectOption(option)"
      >
        {{ option.label }}
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  selected: {
    type: String,
    required: true,
  },
  icon: {
    type: [Object, null],
    default: null,
  },
});

const emit = defineEmits(["update:selected"]);

const isOpen = ref(false);
const popoverRef = ref(null);
const popupRef = ref(null);

const currentLabel = computed(() => {
  const match = props.options.find((o) => o.value === props.selected);
  return match ? match.label : props.selected;
});

function togglePopup() {
  isOpen.value = !isOpen.value;
}

function selectOption(option) {
  emit("update:selected", option.value);
  isOpen.value = false;
  if (option.url && option.url !== "#") {
    window.location.href = option.url;
  }
}

function onKeydown(event) {
  if (event.key === "Escape") {
    isOpen.value = false;
    popoverRef.value?.querySelector(".ca-popover__trigger")?.focus();
    return;
  }

  if (!isOpen.value) return;

  const options = popupRef.value?.querySelectorAll(".ca-popover__option");
  if (!options?.length) return;

  const focused = document.activeElement;
  const idx = Array.from(options).indexOf(focused);

  if (event.key === "ArrowDown") {
    event.preventDefault();
    if (idx === -1) {
      options[0].focus();
    } else {
      const next = idx < options.length - 1 ? idx + 1 : 0;
      options[next].focus();
    }
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    if (idx === -1) {
      options[options.length - 1].focus();
    } else {
      const prev = idx > 0 ? idx - 1 : options.length - 1;
      options[prev].focus();
    }
  }
}

function onClickOutside(event) {
  if (popoverRef.value && !popoverRef.value.contains(event.target)) {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", onClickOutside);
});
</script>

<style scoped lang="scss">
.ca-popover {
  position: relative;
  display: inline-flex;

  @include ca-bp("md") {
    min-width: 120px;
  }
}

.ca-popover__trigger {
  display: flex;
  align-items: center;
  gap: var(--size-3);
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--ca-color-text-inverted);
  white-space: nowrap;
  width: 100%;
}

.ca-popover__icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.ca-popover__label {
  font-size: var(--font-size-sm);
  font-weight: 600;
  line-height: var(--line-height-sm);
  letter-spacing: var(--letter-spacing-sm);

  @include ca-bp("md") {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    letter-spacing: normal;
  }
}

.ca-popover__popup {
  position: absolute;
  left: 0;
  bottom: 38px;
  display: flex;
  width: 162px;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--size-3);
  background: var(--ca-color-text-inverted);
  box-shadow: 0 4px 8px 3px rgba(0, 0, 0, 0.15);
  z-index: 10;

  @include ca-bp("md") {
    left: auto;
    right: 0;
    bottom: 100%;
    margin-bottom: var(--size-3);
  }
}

.ca-popover__option {
  display: flex;
  min-height: var(--size-8);
  padding: 0 var(--size-5);
  justify-content: center;
  align-items: center;
  align-self: stretch;
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--ca-color-text-body);
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  outline: none;
  transition: background-color 0.15s ease;

  &:focus-visible {
    outline: 2px solid var(--ca-color-feedback-info);
    outline-offset: -2px;
  }

  &:hover {
    background-color: var(--ca-devs-secondary-pressed);
    opacity: 1;
  }

  &--active {
    background-color: var(--ca-devs-secondary-pressed);
    color: var(--ca-color-theme-primary);
    font-weight: 600;
  }
}
</style>
